import React from "react";
import { Badge, Card } from "react-bootstrap";
import { Activity, CheckCircle, XCircle } from "react-feather";
import {
  IFullAutoCouponsTargetingSDKTestSummary,
  ISdkTestBasketTest,
  ISdkTestTranslationTest,
  ITesterProfileSnapshot,
  sdkTestCheckoutTimingLabel,
  sdkTestLanguageLabel,
  sdkTestSampleAppLabel,
} from "../../interfaces";

/** The snapshot summary, plus the extras a live test record carries. */
export type SdkTestCardTest = IFullAutoCouponsTargetingSDKTestSummary & {
  tester?: ITesterProfileSnapshot | null;
  translationTest?: ISdkTestTranslationTest | null;
};

/** A single checkout test result — pass/fail colour + timing bucket. */
const CheckoutPill: React.FC<{
  label: string;
  result: "pass" | "fail";
  timing: string;
}> = ({ label, result, timing }) => (
  <div className="me-3">
    <div className="text-muted" style={{ fontSize: 10 }}>
      {label}
    </div>
    <Badge bg={result === "pass" ? "success" : "danger"}>
      {result === "pass" ? (
        <CheckCircle size={10} className="me-1" />
      ) : (
        <XCircle size={10} className="me-1" />
      )}
      {sdkTestCheckoutTimingLabel(timing)}
    </Badge>
  </div>
);

const BasketPills: React.FC<{ prefix: string; test: ISdkTestBasketTest }> = ({
  prefix,
  test,
}) => (
  <>
    <CheckoutPill
      label={`${prefix} 1`}
      result={test.firstCheckoutResult}
      timing={test.firstCheckoutTiming}
    />
    <CheckoutPill
      label={`${prefix} 2`}
      result={test.secondCheckoutResult}
      timing={test.secondCheckoutTiming}
    />
  </>
);

/**
 * The most recent APK/SDK test at a glance. Shown wherever a tester might
 * otherwise have to open the recorder just to read the last result.
 */
export const SdkTestResultsCard: React.FC<{
  test: SdkTestCardTest;
  className?: string;
}> = ({ test, className = "mb-3" }) => (
  <Card className={className}>
    <Card.Header className="py-2 small fw-bold d-flex justify-content-between align-items-center">
      <span>
        <Activity size={13} className="me-1" />
        SDK test results
      </span>
      <Badge bg={test.result === "pass" ? "success" : "danger"}>
        Overall: {test.result === "pass" ? "Pass" : "Fail"}
      </Badge>
    </Card.Header>
    <Card.Body className="small">
      <div className="d-flex flex-wrap align-items-end gap-2 mb-2">
        <div className="me-3">
          <div className="text-muted" style={{ fontSize: 10 }}>
            Sample app
          </div>
          <span className="fw-semibold">
            {sdkTestSampleAppLabel(test.sampleApp)}
          </span>
        </div>
        <BasketPills
          prefix="Single"
          test={{
            firstCheckoutResult: test.firstCheckoutResult,
            firstCheckoutTiming: test.firstCheckoutTiming,
            secondCheckoutResult: test.secondCheckoutResult,
            secondCheckoutTiming: test.secondCheckoutTiming,
          }}
        />
        {test.multiBasketTest && (
          <BasketPills prefix="Multi" test={test.multiBasketTest} />
        )}
        {test.translationTest && (
          <BasketPills
            prefix={`${sdkTestLanguageLabel(
              test.translationTest.languageTested
            )}`}
            test={test.translationTest}
          />
        )}
      </div>
      {test.reason && <div className="text-muted">Notes: {test.reason}</div>}
      <div className="text-muted" style={{ fontSize: 11 }}>
        Recorded {new Date(test.createdAt).toLocaleString()}
        {test.testedBy ? ` by ${test.testedBy}` : ""}
        {test.tester?.label ? ` on ${test.tester.label}` : ""}.
      </div>
    </Card.Body>
  </Card>
);
